import { createApi } from '@reduxjs/toolkit/query/react';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice';
import BaseQuery from './base.api';
import type { PaginatedResults } from './models/paginatedResults.model.ts';
import type { SavedSearch } from './models/savedSearch.model';
import type {
	DocumentDownloadType,
	User,
	UserFormSummary,
	UserSearchParams,
} from './models/user.model';

// Define the API slice
export const usersApi = createApi({
	reducerPath: 'usersApi',
	baseQuery: BaseQuery('/v1/users'),
	tagTypes: ['User', 'SavedSearch'],
	endpoints: (builder) => ({
		getCurrentUser: builder.query<User, void>({
			query: () => ({
				url: '/current',
				headers: {
					'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
				},
			}),
			providesTags: ['User'],
		}),
		// Get all user's saved searches
		getSavedSearches: builder.query<SavedSearch[], void>({
			query: () => {
				return `/current/saved-searches`;
			},
			providesTags: ['SavedSearch'],
		}),
		createSavedSearch: builder.mutation<
			SavedSearch,
			{ name: string; filters: Record<string, any> }
		>({
			query: ({ name, filters }) => ({
				url: `/current/saved-searches`,
				method: 'POST',
				body: { name, filters },
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			invalidatesTags: ['SavedSearch'],
		}),
		updateSavedSearch: builder.mutation<
			SavedSearch,
			{ searchId: number; name: string; filters: Record<string, any> }
		>({
			query: ({ searchId, name, filters }) => ({
				url: `/current/saved-searches/${searchId}`,
				method: 'PUT',
				body: { name, filters },
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			invalidatesTags: ['SavedSearch'],
		}),
		deleteSavedSearch: builder.mutation<void, { searchId: number }>({
			query: ({ searchId }) => ({
				url: `/current/saved-searches/${searchId}`,
				method: 'DELETE',
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			invalidatesTags: ['SavedSearch'],
		}),
		getUsers: builder.query<
			PaginatedResults<User>,
			{
				currentPage: number;
				pageSize: number;
			}
		>({
			query: ({ currentPage, pageSize }) => {
				return `/get-external-users?page=${currentPage}&pageSize=${pageSize}`;
			},
			providesTags: ['User'],
		}),
		userSearch: builder.query<PaginatedResults<User>, UserSearchParams>({
			query: ({ currentPage, pageSize, searchTerm }) =>
				`/search?searchTerm=${encodeURIComponent(searchTerm)}&page=${currentPage}&pageSize=${pageSize}`,
		}),
		validateEmail: builder.query<boolean, { email: string }>({
			query: ({ email }) => ({
				url: `/verify-email?email=${encodeURIComponent(email)}`,
			}),
		}),
		getUserSummaryByAdmin: builder.query<UserFormSummary, { userId: string | undefined }>({
			query: ({ userId }) => {
				return `summary/${userId}/filtered`;
			},
			providesTags: ['User'],
		}),
		generateOTP: builder.mutation<void, { emailId: string }>({
			query: (body) => ({
				url: '/generate-otp',
				method: 'POST',
				body,
			}),
		}),
		validateOTP: builder.mutation<{ resetToken: string }, { emailId: string; otp: string }>({
			query: (body) => ({
				url: '/validate-otp',
				method: 'POST',
				body,
			}),
		}),
		createPassword: builder.mutation<
			{ token: string },
			{ emailId: string; password: string; token: string }
		>({
			query: (body) => ({
				url: '/create',
				method: 'POST',
				body,
			}),
		}),
		resetPassword: builder.mutation<
			{ message: string },
			{ emailId: string; newPassword: string; resetToken: string }
		>({
			query: (body) => ({
				url: '/reset-password',
				method: 'POST',
				body,
			}),
		}),
		documentDownload: builder.query<string, { documentDownloadType: DocumentDownloadType }>({
			query: ({ documentDownloadType }) => ({
				url: `/document-download?documentDownloadType=${documentDownloadType}`,
				responseHandler: async (response: any) => URL.createObjectURL(await response.blob()),
			}),
		}),
		acceptTerms: builder.mutation<void, void>({
			query: () => ({
				url: '/accept-terms',
				method: 'PUT',
			}),
			invalidatesTags: ['User'],
		}),
	}),
});

export const {
	useGetCurrentUserQuery,
	useGetSavedSearchesQuery,
	useCreateSavedSearchMutation,
	useUpdateSavedSearchMutation,
	useDeleteSavedSearchMutation,
	useLazyGetUsersQuery,
	useAcceptTermsMutation,
	useLazyUserSearchQuery,
	useLazyValidateEmailQuery,
	useGenerateOTPMutation,
	useValidateOTPMutation,
	useCreatePasswordMutation,
	useResetPasswordMutation,
	useGetUserSummaryByAdminQuery,
	useDocumentDownloadQuery,
} = usersApi;
